import { CommonModule, isPlatformBrowser, NgClass } from '@angular/common';
import {
  Component,
  computed,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  SimpleChanges,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { PermissionValidatorService } from '@nomoh-monorepo/data-access-auth';
import { PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { ItemMenu } from '../interfaces/Item-menu.interface';

@Component({
  selector: 'lib-feature-s-navigate',
  standalone: true,
  templateUrl: './feature-s-navigate.component.html',
  styleUrls: ['./feature-s-navigate.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    NgClass,
    TooltipModule,
    PopoverModule,
    TranslateModule,
  ],
})
export class FeatureSNavigateComponent implements OnInit, OnChanges, OnDestroy {
  private router = inject(Router);
  private permissionService = inject(PermissionValidatorService);
  private platformId = inject(PLATFORM_ID);

  @Input() items: ItemMenu[] = [];
  @Input() maxVisibleItems = 4;

  selected!: ItemMenu;
  menuVisivel = true;
  private routerSubscription?: Subscription;
  private _lastScrollY = 0;
  private _lastScrollTarget: EventTarget | null = null;
  private _isMobile = false;
  private _ignoreScrollUntil = 0;
  private _scrollListener?: (event: Event) => void;

  visibleItems = signal<ItemMenu[]>([]);
  overflowItems = signal<ItemMenu[]>([]);
  hasOverflow = computed(() => this.overflowItems().length > 0);

  ngOnInit() {
    this.updateItemsDistribution();
    this.selectCurrentRoute();

    if (isPlatformBrowser(this.platformId)) {
      this._isMobile = window.innerWidth <= 768;
      this.bindScrollListener();
    }

    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this._ignoreScrollUntil = Date.now() + 500;
        this._lastScrollTarget = null;
        this.menuVisivel = true;
        this.selectCurrentRoute();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['items'] || changes['maxVisibleItems']) {
      this.updateItemsDistribution();
    }
  }

  private updateItemsDistribution() {
    const itemsComPermissao = this.items.filter((item) =>
      this.hasPermission(item.permission)
    );

    if (itemsComPermissao.length <= this.maxVisibleItems) {
      this.visibleItems.set(itemsComPermissao);
      this.overflowItems.set([]);
    } else {
      const limit = this.maxVisibleItems - 1;
      this.visibleItems.set(itemsComPermissao.slice(0, limit));
      this.overflowItems.set(itemsComPermissao.slice(limit));
    }
  }

  private selectCurrentRoute() {
    const current = this.items.find(
      (i) =>
        i.link &&
        this.router.isActive(i.link, {
          paths: 'exact',
          queryParams: 'ignored',
          fragment: 'ignored',
          matrixParams: 'ignored',
        })
    );
    if (current) this.selected = current;
  }

  /** Scroll em captura: pega scroll de divs internas (não só window) */
  private bindScrollListener() {
    this._scrollListener = (event: Event) => {
      if (!this._isMobile || Date.now() < this._ignoreScrollUntil) return;

      const currentScrollY = this.getScrollTop(event.target);

      if (event.target !== this._lastScrollTarget) {
        this._lastScrollTarget = event.target;
        this._lastScrollY = currentScrollY;
        return;
      }

      if (currentScrollY > this._lastScrollY + 10) {
        this.menuVisivel = false;
      } else if (currentScrollY < this._lastScrollY - 10) {
        this.menuVisivel = true;
      }
      this._lastScrollY = currentScrollY;
    };

    document.addEventListener('scroll', this._scrollListener, true);
  }

  private getScrollTop(target: EventTarget | null): number {
    if (!target || target === document || target === document.documentElement) {
      return window.scrollY;
    }
    if (target === document.body) {
      return window.scrollY || document.body.scrollTop;
    }
    return (target as HTMLElement).scrollTop ?? 0;
  }

  @HostListener('window:resize')
  onResize() {
    if (!isPlatformBrowser(this.platformId)) return;
    this._isMobile = window.innerWidth <= 768;
    if (!this._isMobile) {
      this.menuVisivel = true;
    }
  }

  select(item: ItemMenu) {
    this.selected = item;
    this._ignoreScrollUntil = Date.now() + 500;
    this.menuVisivel = true;
    if (item.link) this.router.navigate([item.link]);
  }

  toggleMenu() {
    this.menuVisivel = !this.menuVisivel;
  }

  hasPermission(permission: string | undefined): boolean {
    if (!permission) return true;
    return this.permissionService.hasPermission(permission);
  }

  trackByLabel = (_: number, item: ItemMenu) => item.label;

  ngOnDestroy() {
    this.routerSubscription?.unsubscribe();
    if (this._scrollListener && isPlatformBrowser(this.platformId)) {
      document.removeEventListener('scroll', this._scrollListener, true);
    }
  }
}
